import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/authContext";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";

import { Button, Form, Input, message } from "antd";
import LoginApple from "./LoginApple";
import LoginGoogle from "./LoginGoogle";
import LoginOtpVerification from "./LoginOtpVerification";

import { getProfile, loginEmail, loginPhone } from "../../api";

import PhoneNotVerifyPopup from "../../components/common/PhoneNotVerifyPopup";
import {
  convertPhone,
  isEmailValid,
  isPhoneValid,
  saveSession,
  showMessage,
} from "../../utils/common";

interface Props {}

const Login: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const auth = useAuth();
  const history = useHistory();
  const [phoneNotVerify, setPhoneNotVerify] = useState<{
    phone: string;
    emails: string[];
  }>();
  const [popupToShow, setPopupToShow] = useState<"NOTIFY_PHONE" | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [accountNotExist, setAccountNotExist] = useState<boolean>(false);
  const [otpChallenge, setOtpChallenge] = useState<{
    email: string;
    session_id: number;
  } | null>(null);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const callbackUrl = query.get("callbackUrl");
    if (callbackUrl) {
      const callbackDomain = new URL(callbackUrl).hostname;
      console.log("callbackDomain", callbackDomain);
      const allowedDomains =
        process.env.REACT_APP_CALLBACK_DOMAINS?.split(",") || [];

      console.log("allowedDomains", allowedDomains);
      if (
        !process.env.REACT_APP_CALLBACK_DOMAINS ||
        !process.env.REACT_APP_CALLBACK_DOMAINS.split(",").includes(
          callbackDomain,
        )
      ) {
        showMessage("error", "Invalid Callback URL");
        history.push("/login");
      }
    }
  }, []);

  const onClickRegisterWithEmailOrPhone = () => {
    const query = new URLSearchParams(window.location.search);
    if (form.getFieldValue("username")) {
      query.set("emailOrPhone", form.getFieldValue("username"));
    }
    history.push(`/register${query.toString() ? `?${query.toString()}` : ""}`);
  };

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading(true);
      const isEmail = isEmailValid(values?.username);
      const res = isEmail
        ? await loginEmail({
            email: values?.username,
            password: values?.password,
          })
        : await loginPhone({
            phone: convertPhone(values?.username),
            password: values?.password,
          });
      if (res.status === "error") {
        showMessage("error", res.message);
        if (res.code === "CUS_USER_NOT_FOUND") {
          setAccountNotExist(true);
        }
        return;
      }
      if (isEmail && res.data?.required_otp) {
        setOtpChallenge({
          email: values.username,
          session_id: res.data.session_id,
        });
        return;
      }
      console.log("---res---", res);
      onLoginSuccess(
        res.data.access_token,
        res.data.refresh_token,
        res.data.expires_in,
      );
    } catch (error: any) {
      message.error(
        error.response?.data?.message ||
          "Đăng nhập thất bại, vui lòng thử lại sau.",
      );
    } finally {
      setIsLoading(false);
    }
  };
  console.log("otpChallenge", otpChallenge);
  const onLoginSuccess = async (
    accessToken: string,
    refreshToken: string,
    expires: number,
  ) => {
    saveSession(accessToken, refreshToken, expires);

    const query = new URLSearchParams(window.location.search);
    const callbackUrl = query.get("callbackUrl");
    const state = query.get("state");
    const resProfile = await getProfile();

    if (!callbackUrl) {
      if (resProfile.status === "success") {
        auth.login(resProfile.data);
        history.push("/profile");
      }
      return;
    }
    window.location.href = `${callbackUrl}${state ? `?state=${state}` : ""}`;
  };

  if (otpChallenge) {
    return (
      <LoginOtpVerification
        email={otpChallenge?.email || ""}
        sessionId={otpChallenge?.session_id || 0}
        onLoginSuccess={onLoginSuccess}
        onBack={() => setOtpChallenge(null)}
      />
    );
  }

  return (
    <div className="login container mx-auto mt-16 md:mt-40 mb-16 md:mb-40 main">
      <div className="bg-white mx-auto p-16 max-w-[472px] rounded-sm overflow-hidden">
        <div>
          <p className="text-2xl text-center font-semibold">Đăng nhập</p>

          <div className="my-36">
            <Form
              form={form}
              size="large"
              layout="vertical"
              onFinish={handleSubmit}
            >
              <Form.Item
                name="username"
                label={"Email hoặc số điện thoại"}
                required
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value) {
                        return Promise.reject(
                          new Error("Vui lòng nhập email hoặc số điện thoại"),
                        );
                      } else if (!isEmailValid(value) && !isPhoneValid(value)) {
                        return Promise.reject(
                          new Error(
                            "Email hoặc số điện thoại không đúng định dạng",
                          ),
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input type="text" placeholder={"Email hoặc số điện thoại"} />
              </Form.Item>

              <Form.Item
                name="password"
                label={"Mật khẩu"}
                required
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value) {
                        return Promise.reject(
                          new Error("Vui lòng nhập mật khẩu"),
                        );
                      } else if (value?.trim()?.length < 4) {
                        return Promise.reject(
                          new Error("Mật khẩu phải có ít nhất 4 ký tự"),
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input.Password placeholder={"Mật khẩu"} />
              </Form.Item>

              {accountNotExist ? (
                <p style={{ color: "#ff4d4f" }}>
                  Tài khoản không tồn tại.&nbsp;
                  <a
                    style={{ fontWeight: 600, textDecoration: "underline" }}
                    onClick={onClickRegisterWithEmailOrPhone}
                  >
                    Tạo tài khoản
                  </a>
                </p>
              ) : null}
              <div className="d-flex">
                <Button type="link" className="!pl-0">
                  <Link to={`/forgot-password${window.location.search}`}>
                    Quên mật khẩu?
                  </Link>
                </Button>
              </div>

              <Button
                type="primary"
                block={true}
                htmlType="submit"
                loading={isLoading}
                className="!bg-red-primary"
              >
                Đăng nhập
              </Button>

              <div className="border-b solid border-grey-1 my-24 relative">
                <span className="absolute bg-white px-30 left-1/2 -translate-x-1/2 -translate-y-1/2 ">
                  Hoặc
                </span>
              </div>
              {/* login social */}
              <div className="flex flex-col items-center gap-24">
                {/* <div className="w-full">
                  <LoginFacebook onLoginSuccess={onLoginSuccess} />
                </div> */}
                <div className="w-full">
                  <LoginGoogle onLoginSuccess={onLoginSuccess} />
                </div>
                <div className="w-full">
                  <LoginApple onLoginSuccess={onLoginSuccess} />
                </div>
              </div>
            </Form>
          </div>

          <p>
            Chưa có tài khoản?&nbsp;
            <Link
              to={`/register${window.location.search}`}
              className="text-red-primary font-medium"
            >
              Đăng ký
            </Link>
          </p>
        </div>

        {phoneNotVerify && popupToShow === "NOTIFY_PHONE" ? (
          <PhoneNotVerifyPopup
            isRegister={false}
            phone={phoneNotVerify.phone}
            emails={phoneNotVerify.emails}
            onCancel={() => {
              setPopupToShow(null);
              setPhoneNotVerify(undefined);
            }}
          />
        ) : null}
      </div>
    </div>
  );
};

export default Login;
